import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Alert, AlertDescription } from '../../ui/alert';
import { Progress } from '../../ui/progress';
import { useToast } from '../../ui/use-toast';
import { 
  FileSpreadsheet,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Users,
  Package,
  Download,
  Square,
  Loader2,
  ChevronRight,
  ClipboardCheck
} from 'lucide-react';
import { User } from '../../../types';
import { apiService, ApiUser, ApiWorkEntry } from '../../../services/api';
import { exportFinalReportToExcel } from '../../../utils/exportUtils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { RealTimeContext } from '../../../contexts/RealTimeContext';

interface EndOfDayTabProps {
  supervisorId: number;
  currentUser: User;
  teamId?: number;
  teamName?: string;
}

interface TeamMemberStatus {
  user: ApiUser;
  workEntry?: ApiWorkEntry;
  hasActiveWork: boolean;
  hasMaterials: boolean;
  todayHours: number;
}

interface WizardStep {
  id: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  isComplete: boolean;
}

export const EndOfDayTab: React.FC<EndOfDayTabProps> = ({
  supervisorId,
  currentUser,
  teamId,
  teamName
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [teamStatus, setTeamStatus] = useState<TeamMemberStatus[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastBulkOperationTime, setLastBulkOperationTime] = useState<number>(0);
  const [reportNotes, setReportNotes] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [wizardSteps, setWizardSteps] = useState<WizardStep[]>([
    {
      id: 1,
      title: 'Verificare Echipă',
      description: 'Verificați statusul membrilor echipei',
      icon: <Users className="h-5 w-5" />,
      isComplete: false
    },
    {
      id: 2,
      title: 'Oprire Muncă',
      description: 'Opriți munca pentru toți membrii activi',
      icon: <Square className="h-5 w-5" />,
      isComplete: false
    },
    {
      id: 3,
      title: 'Verificare Materiale',
      description: 'Verificați consumul de materiale',
      icon: <Package className="h-5 w-5" />,
      isComplete: false
    },
    {
      id: 4,
      title: 'Generare Raport',
      description: 'Generați raportul final și exportați',
      icon: <FileSpreadsheet className="h-5 w-5" />,
      isComplete: false
    }
  ]);
  const { toast } = useToast();
  
  // Use context safely without throwing
  const realTimeContext = useContext(RealTimeContext);
  const subscribeToTeamMemberStatusChanged = realTimeContext?.subscribeToTeamMemberStatusChanged || (() => () => {});
  const subscribeToWorkStarted = realTimeContext?.subscribeToWorkStarted || (() => () => {});
  const subscribeToWorkEnded = realTimeContext?.subscribeToWorkEnded || (() => () => {});

  useEffect(() => {
    loadTeamStatus();
    
    // Listen for refresh events
    const handleRefresh = () => loadTeamStatus();
    window.addEventListener('supervisor-refresh', handleRefresh);
    
    return () => {
      window.removeEventListener('supervisor-refresh', handleRefresh);
    };
  }, [supervisorId]);

  // Subscribe to SignalR events for real-time updates
  useEffect(() => {
    if (!realTimeContext || !realTimeContext.state.isConnected) {
      console.warn('RealTime context not available or not connected');
      return;
    }

    console.log('Setting up SignalR subscriptions for EndOfDayTab');
    
    // Handle team member status changes
    const unsubscribeTeamStatus = subscribeToTeamMemberStatusChanged((event) => {
      console.log('SignalR: TeamMemberStatusChanged event in EndOfDayTab', event);
      
      // Update specific team member without full reload
      setTeamStatus(prev => prev.map(member => {
        if (member.user.id === event.userId) {
          return {
            ...member,
            hasActiveWork: event.isWorking || false,
            user: {
              ...member.user,
              isWorking: event.isWorking || false,
              onBreak: event.onBreak || false,
              currentWorkEntryId: event.currentWorkEntryId,
              currentBreakId: event.currentBreakId
            }
          };
        }
        return member;
      }));
    });

    // Handle work started
    const unsubscribeWorkStarted = subscribeToWorkStarted((event) => {
      console.log('SignalR: WorkStarted event in EndOfDayTab', event);
      
      setTeamStatus(prev => prev.map(member => {
        if (member.user.id === event.userId) {
          return {
            ...member,
            hasActiveWork: true,
            workEntry: event.workEntry,
            user: {
              ...member.user,
              isWorking: true,
              currentWorkEntryId: event.workEntry?.id
            }
          };
        }
        return member;
      }));
    });

    // Handle work ended
    const unsubscribeWorkEnded = subscribeToWorkEnded((event) => {
      console.log('SignalR: WorkEnded event in EndOfDayTab', event);
      
      setTeamStatus(prev => prev.map(member => {
        if (member.user.id === event.userId) {
          return {
            ...member,
            hasActiveWork: false,
            workEntry: undefined,
            user: {
              ...member.user,
              isWorking: false,
              currentWorkEntryId: null
            }
          };
        }
        return member;
      }));
    });

    return () => {
      unsubscribeTeamStatus();
      unsubscribeWorkStarted();
      unsubscribeWorkEnded();
    };
  }, [realTimeContext, realTimeContext?.state.isConnected, subscribeToTeamMemberStatusChanged, subscribeToWorkStarted, subscribeToWorkEnded]);

  const loadTeamStatus = async (ignoreRecentOperation = false) => {
    // Protect against loading stale data immediately after bulk operations
    if (!ignoreRecentOperation) {
      const timeSinceBulkOp = Date.now() - lastBulkOperationTime;
      if (timeSinceBulkOp < 3000) { // 3 seconds protection window for optimized operations
        console.log('Skipping loadTeamStatus - recent bulk operation', timeSinceBulkOp + 'ms ago');
        return;
      }
    }
    try {
      setIsLoading(true);
      
      // Get team members with their status from User model
      let members = await apiService.getTeamMembers(supervisorId);
      
      // Filter by teamId if provided (for multi-team supervisors)
      if (teamId) {
        members = members.filter(m => m.teamId === teamId);
        console.log(`Team members loaded for team ${teamId}:`, members.map(m => ({
          id: m.id,
          name: m.name,
          isWorking: m.isWorking,
          currentWorkEntryId: m.currentWorkEntryId
        })));
      } else {
        console.log('Team members loaded:', members.map(m => ({
          id: m.id,
          name: m.name,
          isWorking: m.isWorking,
          currentWorkEntryId: m.currentWorkEntryId
        })));
      }
      
      // Get today's work entries
      const today = new Date().toISOString().split('T')[0];
      const workEntriesResponse = await apiService.getWorkEntries(today);
      const workEntries = Array.isArray(workEntriesResponse) 
        ? workEntriesResponse 
        : (workEntriesResponse.data || []);

      // Fetch active work entries for working members in parallel
      const activeEntryPromises = members.map(member => {
        if (member.isWorking && member.currentWorkEntryId) {
          return apiService.getActiveWorkEntry(member.id).catch(err => {
            console.error(`Failed to get active entry for user ${member.id}:`, err);
            return null;
          });
        }
        return Promise.resolve(null);
      });
      
      const activeEntries = await Promise.all(activeEntryPromises);
      const activeEntriesMap = new Map(
        activeEntries
          .filter(e => e !== null)
          .map(e => [e!.userId, e])
      );

      // Process team status - now using IsWorking from User model
      const teamStatusData: TeamMemberStatus[] = await Promise.all(
        members.map(async (member) => {
          const memberEntries = workEntries.filter(entry => entry.userId === member.id);
          
          // Use IsWorking from user model to determine if member has active work
          const hasActiveWork = member.isWorking || false;
          
          // Get active entry from map or from today's entries
          let activeEntry = activeEntriesMap.get(member.id);
          if (!activeEntry && hasActiveWork && member.currentWorkEntryId) {
            activeEntry = memberEntries.find(entry => entry.id === member.currentWorkEntryId);
          }
          
          // Calculate today's hours
          const todayHours = memberEntries.reduce((total, entry) => {
            if (entry.startTime) {
              const start = new Date(entry.startTime);
              const end = entry.endTime ? new Date(entry.endTime) : new Date();
              const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
              const breakMinutes = (entry as { totalBreakMinutes?: number; breakMinutes?: number }).totalBreakMinutes || (entry as { breakMinutes?: number }).breakMinutes || 0;
              return total + hours - breakMinutes / 60;
            }
            return total;
          }, 0);

          // Check if has materials (only for today's entries to optimize)
          let hasMaterials = false;
          for (const entry of memberEntries) {
            try {
              const materials = await apiService.getMaterialConsumptions(entry.id);
              if (materials && materials.length > 0) {
                hasMaterials = true;
                break;
              }
            } catch (error) {
              console.error('Error checking materials:', error);
            }
          }

          console.log(`Member ${member.name}: isWorking=${hasActiveWork}, hasEntry=${!!activeEntry}, todayHours=${todayHours.toFixed(1)}`);

          return {
            user: member,
            workEntry: activeEntry,
            hasActiveWork,
            hasMaterials,
            todayHours
          };
        })
      );

      setTeamStatus(teamStatusData);
      
      // Update step 1 completion
      updateStepCompletion(1, true);
      
    } catch (error) {
      console.error('Error loading team status:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele echipei',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateStepCompletion = (stepId: number, isComplete: boolean) => {
    setWizardSteps(steps => 
      steps.map(step => 
        step.id === stepId ? { ...step, isComplete } : step
      )
    );
  };

  const handleStopAllWork = async () => {
    setIsLoading(true);
    const activeMembers = teamStatus.filter(m => m.hasActiveWork);
    
    if (activeMembers.length === 0) {
      toast({
        title: 'Info',
        description: 'Nu există membri activi de oprit',
      });
      setIsLoading(false);
      updateStepCompletion(2, true);
      setCurrentStep(3);
      return;
    }

    try {
      // First, try to get work entry IDs for active members
      const workEntryIds: number[] = [];
      const membersWithoutEntries: ApiUser[] = [];
      
      for (const member of activeMembers) {
        if (member.workEntry && member.workEntry.id) {
          workEntryIds.push(member.workEntry.id);
        } else if (member.user.currentWorkEntryId) {
          // Try to fetch the work entry if we only have the ID
          try {
            const entry = await apiService.getWorkEntry(member.user.currentWorkEntryId);
            if (entry && entry.id) {
              workEntryIds.push(entry.id);
            }
          } catch (error) {
            console.error(`Failed to fetch work entry ${member.user.currentWorkEntryId}:`, error);
            membersWithoutEntries.push(member.user);
          }
        } else {
          membersWithoutEntries.push(member.user);
        }
      }
      
      // Use bulk stop work API if we have work entries
      let successCount = 0;
      let errorCount = 0;
      
      if (workEntryIds.length > 0) {
        const result = await apiService.bulkStopWork(
          workEntryIds,
          'Închis de supervisor la sfârșitul zilei'
        );
        successCount = result.successCount;
        errorCount = result.errorCount;
      }
      
      // Handle members without work entries (edge case)
      if (membersWithoutEntries.length > 0) {
        console.warn('Members without work entries but marked as working:', membersWithoutEntries);
        errorCount += membersWithoutEntries.length;
      }

      // Update local state immediately for better UX
      setTeamStatus(prevStatus => 
        prevStatus.map(member => {
          if (activeMembers.some(am => am.user.id === member.user.id)) {
            return {
              ...member,
              hasActiveWork: false,
              workEntry: undefined,
              user: {
                ...member.user,
                isWorking: false,
                currentWorkEntryId: null,
                currentBreakId: null
              }
            };
          }
          return member;
        })
      );
      
      // Mark the time of this bulk operation
      setLastBulkOperationTime(Date.now());

      if (successCount > 0) {
        toast({
          title: 'Succes',
          description: `Munca oprită pentru ${successCount} membri`,
        });
      }

      if (errorCount > 0) {
        toast({
          title: 'Atenție',
          description: `${errorCount} membri nu au putut fi opriți`,
          variant: 'destructive'
        });
      }

      updateStepCompletion(2, true);
      
      // Wait longer before reloading to ensure backend has fully processed the bulk operation
      // The optimistic UI update above already shows the correct state
      setTimeout(async () => {
        console.log('Reloading team status after bulk stop operation...');
        await loadTeamStatus(true); // Ignore recent operation check for this forced refresh
        
        // Check if all workers are now stopped
        const updatedStatus = await apiService.getTeamMembers(supervisorId);
        const hasActiveWorkers = updatedStatus.some(m => m.isWorking);
        if (!hasActiveWorkers) {
          setCurrentStep(3);
        } else {
          console.warn('Some workers still active after bulk stop - may need manual intervention');
        }
      }, 2000); // 2 seconds delay - backend is now optimized for fast status updates
      
    } catch (error) {
      console.error('Error stopping work:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut opri munca pentru echipă',
        variant: 'destructive'
      });
      // Reload to get correct state
      await loadTeamStatus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleMaterialsCheck = () => {
    // In real implementation, this would validate all materials
    // For now, we just mark it as complete
    updateStepCompletion(3, true);
    toast({
      title: 'Verificare completă',
      description: 'Materialele au fost verificate',
    });
    // Move to next step
    setCurrentStep(4);
  };

  const handleGenerateReport = () => {
    setShowConfirmDialog(true);
  };

  const confirmGenerateReport = async () => {
    setIsLoading(true);
    setShowConfirmDialog(false);

    try {
      // First check if report already exists
      const today = new Date().toISOString().split('T')[0];
      
      // Try to generate supervisor end-of-day report
      let reportResult;
      try {
        reportResult = await apiService.createSupervisorEndOfDayReport(
          today,
          new Date().toISOString(),
          reportNotes,
          teamId
        );
      } catch (error: any) {
        // If report already exists, ask user if they want to update it
        if (error.message?.includes('Raportul sfârșit zi') || error.message?.includes('deja creat')) {
          toast({
            title: "Raport Existent",
            description: "Un raport pentru această dată există deja. Descarcați raportul Excel pentru a vedea datele actualizate.",
            variant: "default"
          });
          
          // Continue with Excel export even if report exists
          reportResult = { success: true, skipDatabaseUpdate: true };
        } else {
          throw error;
        }
      }

      if (reportResult.success) {
        // Get all work entries for export
        const today = new Date().toISOString().split('T')[0];
        const workEntriesResponse = await apiService.getWorkEntries(today);
        const workEntries = Array.isArray(workEntriesResponse) 
          ? workEntriesResponse 
          : (workEntriesResponse.data || []);

        // Filter for team members
        const teamMemberIds = teamStatus.map(m => m.user.id);
        const teamWorkEntries = workEntries.filter(entry => 
          teamMemberIds.includes(entry.userId)
        );

        // Prepare report data
        const reportData = teamStatus.map(member => ({
          'Nume': member.user.name,
          'Rol': member.user.role === 'worker' ? 'Muncitor' : member.user.role,
          'Data': today,
          'Ore Lucrate': `${member.todayHours.toFixed(1)}h`,
          'Ore Numerice': member.todayHours.toFixed(2),
          'Status Final': member.hasActiveWork ? 'Încă Activ' : 'Finalizat',
          'Overtime': member.todayHours > 8 ? 'Da' : 'Nu',
          'Ore Overtime': member.todayHours > 8 ? (member.todayHours - 8).toFixed(2) : '0'
        }));

        // Export to Excel
        const teamNameSlug = teamName ? `-${teamName.replace(/\s+/g, '-')}` : '';
        const filename = `raport-final-${currentUser.name.replace(/\s+/g, '-')}${teamNameSlug}-${today}.xlsx`;
        await exportFinalReportToExcel(reportData, teamWorkEntries, filename);

        updateStepCompletion(4, true);
        
        toast({
          title: 'Raport generat cu succes',
          description: `Raport salvat și exportat pentru ${teamStatus.length} membri${teamName ? ` din echipa ${teamName}` : ''}`,
        });

        // Reset wizard
        setTimeout(() => {
          setCurrentStep(1);
          setWizardSteps(steps => steps.map(s => ({ ...s, isComplete: false })));
          setReportNotes('');
        }, 2000);
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut genera raportul',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const canProceedToNextStep = () => {
    if (currentStep === 1) return true; // Can always proceed from team check
    if (currentStep === 2) return !teamStatus.some(m => m.hasActiveWork); // All work must be stopped
    if (currentStep === 3) return true; // Can proceed after material check
    return false;
  };

  const getProgressPercentage = () => {
    const completedSteps = wizardSteps.filter(s => s.isComplete).length;
    return (completedSteps / wizardSteps.length) * 100;
  };

  const activeMembers = teamStatus.filter(m => m.hasActiveWork);
  const membersWithMaterials = teamStatus.filter(m => m.hasMaterials);

  return (
    <div className="space-y-6">
      {/* Progress Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardCheck className="h-5 w-5" />
            Wizard Închidere Zi{teamName ? ` - ${teamName}` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Progress value={getProgressPercentage()} className="h-3" />
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {wizardSteps.map((step) => (
              <div
                key={step.id}
                className={`flex flex-col items-center text-center p-3 rounded-lg border-2 transition-all cursor-pointer
                  ${currentStep === step.id ? 'border-blue-500 bg-blue-50' : ''}
                  ${step.isComplete ? 'border-green-500 bg-green-50' : 'border-gray-200'}
                  ${step.id < currentStep && !step.isComplete ? 'border-orange-300 bg-orange-50' : ''}
                `}
                onClick={() => setCurrentStep(step.id)}
              >
                <div className={`mb-2 ${step.isComplete ? 'text-green-600' : 'text-gray-600'}`}>
                  {step.isComplete ? <CheckCircle className="h-8 w-8" /> : step.icon}
                </div>
                <h4 className="font-medium text-sm">{step.title}</h4>
                <p className="text-xs text-gray-600 mt-1">{step.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Step Content */}
      {currentStep === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Verificare Status Echipă{teamName ? ` ${teamName}` : ''}</span>
              <Badge variant={activeMembers.length > 0 ? "warning" : "success"}>
                {activeMembers.length} activi
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Membri</p>
                      <p className="text-2xl font-bold">{teamStatus.length}</p>
                    </div>
                    <Users className="h-8 w-8 text-gray-400" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Încă Lucrează</p>
                      <p className="text-2xl font-bold text-orange-600">{activeMembers.length}</p>
                    </div>
                    <Clock className="h-8 w-8 text-orange-400" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Cu Materiale</p>
                      <p className="text-2xl font-bold text-blue-600">{membersWithMaterials.length}</p>
                    </div>
                    <Package className="h-8 w-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-2">
              {teamStatus.map(member => (
                <div key={member.user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{member.user.name}</p>
                    <p className="text-sm text-gray-600">
                      {member.todayHours.toFixed(1)}h lucrate azi
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {member.hasActiveWork ? (
                      <Badge variant="warning">Activ</Badge>
                    ) : (
                      <Badge variant="secondary">Finalizat</Badge>
                    )}
                    {member.hasMaterials && (
                      <Badge variant="outline" className="gap-1">
                        <Package className="h-3 w-3" />
                        Materiale
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={() => setCurrentStep(2)}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2"
            >
              Continuă
              <ChevronRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {currentStep === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Oprire Muncă pentru Toți Membrii</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {activeMembers.length > 0 ? (
              <>
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    {activeMembers.length} membri încă au munca activă. 
                    Apăsați butonul de mai jos pentru a opri munca pentru toți.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  {activeMembers.map(member => (
                    <div key={member.user.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                      <div>
                        <p className="font-medium">{member.user.name}</p>
                        <p className="text-sm text-gray-600">
                          Activ din {member.workEntry && new Date(member.workEntry.startTime).toLocaleTimeString('ro-RO')}
                        </p>
                      </div>
                      <Badge variant="warning">Activ</Badge>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={handleStopAllWork}
                  disabled={isLoading}
                  variant="destructive"
                  className="w-full flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                  Oprește Munca pentru Toți
                </Button>
              </>
            ) : (
              <>
                <Alert className="border-green-200 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    Toți membrii echipei au finalizat munca pentru astăzi.
                  </AlertDescription>
                </Alert>

                <Button
                  onClick={() => setCurrentStep(3)}
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2"
                >
                  Continuă
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {currentStep === 3 && (
        <Card>
          <CardHeader>
            <CardTitle>Verificare Consum Materiale</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Package className="h-4 w-4" />
              <AlertDescription>
                Verificați că toate materialele consumate au fost înregistrate corect.
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-blue-600">{membersWithMaterials.length}</p>
                    <p className="text-sm text-gray-600 mt-1">Membri cu materiale înregistrate</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <p className="text-3xl font-bold text-gray-600">
                      {teamStatus.length - membersWithMaterials.length}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">Membri fără materiale</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-2">
              {teamStatus.map(member => (
                <div key={member.user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium">{member.user.name}</p>
                  </div>
                  <div>
                    {member.hasMaterials ? (
                      <Badge variant="success" className="gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Materiale înregistrate
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="gap-1">
                        <XCircle className="h-3 w-3" />
                        Fără materiale
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <Button
              onClick={handleMaterialsCheck}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2"
            >
              Confirmă Verificarea
              <CheckCircle className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {currentStep === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>Generare Raport Final</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className="border-blue-200 bg-blue-50">
              <FileSpreadsheet className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                Raportul final va include toate orele lucrate, materialele consumate și statusul echipei.
              </AlertDescription>
            </Alert>

            <div>
              <Label>Note adiționale pentru raport (opțional)</Label>
              <Textarea
                value={reportNotes}
                onChange={(e) => setReportNotes(e.target.value)}
                placeholder="Adăugați observații despre ziua de lucru..."
                rows={4}
              />
            </div>

            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <h4 className="font-medium">Sumar zi de lucru:</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Total membri:</div>
                <div className="font-medium">{teamStatus.length}</div>
                <div>Ore totale echipă:</div>
                <div className="font-medium">
                  {teamStatus.reduce((sum, m) => sum + m.todayHours, 0).toFixed(1)}h
                </div>
                <div>Membri cu overtime:</div>
                <div className="font-medium">
                  {teamStatus.filter(m => m.todayHours > 8).length}
                </div>
                <div>Status final:</div>
                <div className="font-medium text-green-600">
                  Toți finalizați
                </div>
              </div>
            </div>

            <Button
              onClick={handleGenerateReport}
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2"
              size="lg"
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <Download className="h-5 w-5" />
              )}
              Generează și Descarcă Raport
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmare Generare Raport</DialogTitle>
            <DialogDescription>
              Sunteți sigur că doriți să generați raportul final? 
              Această acțiune va marca ziua de lucru ca încheiată.
            </DialogDescription>
          </DialogHeader>
          
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Asigurați-vă că toți membrii au finalizat munca și toate materialele au fost înregistrate.
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
            >
              Anulează
            </Button>
            <Button
              onClick={confirmGenerateReport}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              Confirmă și Generează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};